import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { ArrowLeft, Users, GraduationCap, Calendar } from "lucide-react";

const MasterSummary = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalInstructors: 0,
    activeInstructors: 0,
    totalBatches: 0,
  });

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        setUserName(profile.full_name);
      }
    };
    checkAuth();
    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      const [studentsRes, instructorsRes, activeInstructorsRes, batchesRes] = await Promise.all([
        supabase.from("students").select("id", { count: "exact", head: true }),
        supabase.from("instructors").select("id", { count: "exact", head: true }),
        supabase.from("instructors").select("id", { count: "exact", head: true }).eq("status", "Active"),
        supabase.from("batches").select("id", { count: "exact", head: true }),
      ]);

      setStats({
        totalStudents: studentsRes.count || 0,
        totalInstructors: instructorsRes.count || 0,
        activeInstructors: activeInstructorsRes.count || 0,
        totalBatches: batchesRes.count || 0,
      });
    } catch (error) {
      toast.error("Failed to load statistics");
    }
  };

  const summaryCards = [
    {
      title: "Total Students",
      value: stats.totalStudents,
      icon: GraduationCap,
      color: "text-blue-600",
    },
    {
      title: "Total Instructors",
      value: stats.totalInstructors,
      icon: Users,
      color: "text-green-600",
    },
    {
      title: "Active Instructors",
      value: stats.activeInstructors,
      icon: Users,
      color: "text-green-600",
    },
    {
      title: "Total Batches",
      value: stats.totalBatches,
      icon: Calendar,
      color: "text-purple-600",
    },
  ];

  return (
    <Layout userName={userName}>
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Master Summary</h2>
          <p className="text-sm sm:text-base text-muted-foreground">Complete overview of all data</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.title}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {card.title}
                    </CardTitle>
                    <Icon className={`h-5 w-5 ${card.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{card.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button onClick={() => navigate("/students")} className="w-full justify-start" variant="outline">
                View All Students
              </Button>
              <Button onClick={() => navigate("/students/add")} className="w-full justify-start" variant="outline">
                Add New Student
              </Button>
              <Button onClick={() => navigate("/instructors/add")} className="w-full justify-start" variant="outline">
                Add New Instructor
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Attendance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button onClick={() => navigate("/attendance/student")} className="w-full justify-start" variant="outline">
                Student Attendance
              </Button>
              <Button onClick={() => navigate("/attendance/instructor")} className="w-full justify-start" variant="outline">
                Instructor Attendance
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default MasterSummary;